/**
 * ---------------------------------------------------------------------------------------------------------------------
 * ADD-ON PRODUCTS
 *
 * Two jobs: keep the displayed product price in sync with the ticked add-ons, and put those add-ons in the cart as
 * their own lines when the product is added.
 *
 * The add-ons are real products that each need their own cart line, which the single add to cart request of the theme
 * cannot express. So the ticked add-ons are pushed to the cart first and the form is then submitted again untouched,
 * letting the theme add the main product and render the mini cart exactly as it always does.
 *
 * The submit listener is bound on the document in the capture phase on purpose. The theme binds its own handler on the
 * form element itself from ProductForm.connectedCallback, which runs before this file, and listeners on the target fire
 * in the order they were added regardless of the capture flag — so a listener on the form would run too late. Capturing
 * on an ancestor is what reliably runs first and lets stopPropagation hold the event back.
 *
 * Careful with properties read off the <form>: HTMLFormElement resolves named controls before its own properties, and
 * the product form contains <input name="id">, so form.id is that input and not the id of the form. Hence
 * getAttribute('id') below, and a WeakSet rather than a data attribute to remember the forms already dealt with.
 *
 * The free offer is not handled here: it carries no product, so it rides along as a line item property on the form.
 * ---------------------------------------------------------------------------------------------------------------------
 */

(function () {
  'use strict';

  var handledForms = new WeakSet();
  var priceState = new WeakMap();
  var observedPrices = new WeakSet();

  /* ================================================================================================================
     PRICE
     ================================================================================================================ */

  function formatMoney(cents) {
    var settings = window.themeVariables.settings;
    var format = settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
    var placeholder = (format.match(/\{\{\s*(\w+)\s*\}\}/) || [, 'amount'])[1];
    var precision = placeholder.indexOf('no_decimals') === -1 ? 2 : 0;
    var thousands = ',';
    var decimal = '.';

    if (placeholder.indexOf('comma_separator') !== -1) {
      thousands = '.';
      decimal = ',';
    } else if (placeholder.indexOf('apostrophe_separator') !== -1) {
      thousands = "'";
    } else if (placeholder.indexOf('space_separator') !== -1) {
      thousands = ' ';
      decimal = ',';
    }

    var parts = (cents / 100).toFixed(precision).split('.');
    var amount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');

    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /* The displayed price is what the add-ons are added to, not the Shopify variant price. The variant price only knows
     about the variant; the product options app charges for its own options on top of it and writes the result out
     here. Reading the variant instead was the bug behind a larger size losing its surcharge the moment an add-on was
     ticked. */
  function parseMoney(text) {
    var digits = String(text).replace(/[^0-9.,]/g, '');

    if (!digits) {
      return null;
    }

    /* A separator with one or two digits behind it and nothing after is the decimal one, whichever character the shop
       formats with; every other separator groups thousands and drops out. */
    var decimalMatch = digits.match(/[.,](\d{1,2})$/);
    var decimals = 0;

    if (decimalMatch) {
      decimals = decimalMatch[1].length === 1 ? parseInt(decimalMatch[1], 10) * 10 : parseInt(decimalMatch[1], 10);
      digits = digits.slice(0, decimalMatch.index);
    }

    var units = parseInt(digits.replace(/[.,]/g, ''), 10);

    return isNaN(units) ? null : units * 100 + decimals;
  }

  function getAddOnTotal(list) {
    return Array.prototype.reduce.call(list.querySelectorAll('[data-add-on-variant]:checked'), function (total, checkbox) {
      return total + (parseInt(checkbox.getAttribute('data-add-on-price'), 10) || 0);
    }, 0);
  }

  /* The amount sits in a bare text node next to a visually hidden "Sale price" label, which has to survive. */
  function moneyNode(element) {
    return Array.prototype.filter.call(element.childNodes, function (node) {
      return node.nodeType === 3 && node.textContent.trim() !== '';
    })[0] || null;
  }

  function applyAddOnTotal(element, addOnTotal) {
    var node = moneyNode(element);

    if (!node) {
      return;
    }

    var current = node.textContent;
    var state = priceState.get(element);

    /* Either this element has not been seen before, or somebody else has rewritten the price since the last pass —
       the theme on a variant change, the app on one of its own options. What is on screen now is the base to add on
       to, and the untouched text is kept so unticking everything restores it exactly rather than reformatting it. */
    if (!state || current !== state.written) {
      state = { base: current, written: current };
      priceState.set(element, state);
    }

    var text = state.base;

    if (addOnTotal > 0) {
      var baseCents = parseMoney(state.base);

      // An amount that cannot be read is left alone rather than guessed at.
      if (baseCents !== null) {
        text = formatMoney(baseCents + addOnTotal);
      }
    }

    if (text !== current) {
      node.textContent = text;
    }

    state.written = text;
  }

  /* The app rewrites the price whenever one of its own options changes, and it does so outside every event this file
     can listen for. Watching the element itself catches it whenever it happens.

     This cannot turn into a tug of war: the pass above recognises its own last write, so a mutation it caused finds
     the base unchanged and produces no new write, and the loop settles after one hop. */
  function observePrice(element) {
    if (observedPrices.has(element)) {
      return;
    }

    observedPrices.add(element);
    new MutationObserver(updatePrices).observe(element, { characterData: true, childList: true, subtree: true });
  }

  function updatePrices() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-add-on-list]'), function (list) {
      var section = list.closest('.shopify-section');

      if (!section) {
        return;
      }

      var addOnTotal = getAddOnTotal(list);
      var priceElements = section.querySelectorAll(
        '[data-block-type="meta"] .price:not(.price--compare), .product-sticky-form__price'
      );

      Array.prototype.forEach.call(priceElements, function (element) {
        applyAddOnTotal(element, addOnTotal);
        observePrice(element);
      });
    });
  }

  document.addEventListener('change', function (event) {
    if (event.target instanceof Element && event.target.hasAttribute('data-add-on-variant')) {
      updatePrices();
    }
  });

  /* variant:changed is dispatched right before the theme swaps the re-rendered blocks in, synchronously, so a task
     queued here runs once the new price is already in the DOM. */
  document.addEventListener('variant:changed', function () {
    setTimeout(updatePrices, 0);
  });

  /* ================================================================================================================
     PLACEMENT
     ================================================================================================================ */

  /* Liquid renders the add-ons as the last sibling before <product-payment-container>, but an app can inject its own
     container into the form afterwards and land between the two. Re-asserting the position puts the add-ons back
     below whatever was injected and directly above the add to cart button.

     This runs a fixed handful of times rather than from a MutationObserver on the form. An observer re-fires on the
     move it just made, and against an app that keeps re-inserting its own container it turns into a tug of war that
     starves the page — which is what stopped the product options from rendering at all. */
  function anchorAddOns() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-add-on-list]'), function (list) {
      var form = document.getElementById(list.getAttribute('data-add-on-form'));

      if (!form) {
        return;
      }

      // The row holding the stepper and the button, falling back to the button container on its own.
      var anchor = form.querySelector('.product-form__purchase') || form.querySelector('product-payment-container');

      if (anchor && anchor.previousElementSibling !== list) {
        anchor.parentNode.insertBefore(list, anchor);
      }
    });
  }

  function init() {
    updatePrices();
    anchorAddOns();

    // Late enough for an app to have injected its own markup into the form, and then it stops.
    window.addEventListener('load', anchorAddOns);
    setTimeout(anchorAddOns, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ================================================================================================================
     HEADER CART COUNT
     ================================================================================================================ */

  /* The count cannot be hung off the events of the theme alone. Its post-add chain reads /cart.js without
     cache: 'reload', so it can be handed a stale cart, and everything it emits — cart:updated, cart:refresh — lives
     inside an async chain with no catch: if any step throws once the item is already added, the events never fire and
     the header keeps the old number until a reload.

     So the events are taken when they come, and an add to cart additionally re-reads the cart a few times on its own
     while the request lands. Nothing global is touched here: wrapping window.fetch to watch for cart requests reached
     further than this file should and broke the scripts of the product options app. */

  var cartSyncTimer = null;

  function syncCartCount() {
    return fetch(window.themeVariables.routes.cartUrl + '.js', { cache: 'reload' }).then(function (response) {
      return response.json();
    }).then(function (cart) {
      Array.prototype.forEach.call(document.querySelectorAll('cart-count'), function (element) {
        element.textContent = cart.item_count;
      });
    }).catch(function () {
      // Nothing to do: the count keeps whatever is already there.
    });
  }

  function scheduleCartCountSync() {
    clearTimeout(cartSyncTimer);
    cartSyncTimer = setTimeout(syncCartCount, 150);
  }

  // Three bounded reads covering a slow add, fired only when the customer actually submits the product form.
  function syncCartCountAfterAdd() {
    [400, 1200, 2500].forEach(function (delay) {
      setTimeout(syncCartCount, delay);
    });
  }

  ['cart:updated', 'cart:refresh'].forEach(function (name) {
    document.documentElement.addEventListener(name, scheduleCartCountSync);
  });

  /* ================================================================================================================
     ADD TO CART
     ================================================================================================================ */

  function getSelectedVariantIds(form) {
    var formId = form.getAttribute('id');

    if (!formId) {
      return [];
    }

    var list = document.querySelector('[data-add-on-list][data-add-on-form="' + formId + '"]');

    if (!list) {
      return [];
    }

    return Array.prototype.map.call(list.querySelectorAll('[data-add-on-variant]:checked'), function (checkbox) {
      return parseInt(checkbox.getAttribute('data-add-on-variant'), 10);
    });
  }

  function setBusy(form, isBusy) {
    Array.prototype.forEach.call(form.elements, function (element) {
      if (element.type !== 'submit') {
        return;
      }

      if (isBusy) {
        element.setAttribute('disabled', 'disabled');
        element.setAttribute('aria-busy', 'true');
      } else {
        element.removeAttribute('disabled');
        element.removeAttribute('aria-busy');
      }
    });
  }

  function addToCart(variantIds) {
    return fetch(window.themeVariables.routes.cartAddUrl + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Without this header the endpoint answers with a redirect instead of a 422 on error.
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: variantIds.map(function (variantId) {
          return { id: variantId, quantity: 1 };
        })
      })
    });
  }

  function reportError(form, message) {
    form.dispatchEvent(new CustomEvent('cart-notification:show', {
      bubbles: true,
      cancelable: true,
      detail: { status: 'error', error: message || '' }
    }));
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') {
      return;
    }

    // Every add to cart goes through here, with or without add-ons, so this is where the count gets watched.
    syncCartCountAfterAdd();

    // Second pass: the add-ons are in the cart already, hand the event over to the theme.
    if (handledForms.has(form)) {
      handledForms.delete(form);
      return;
    }

    var variantIds = getSelectedVariantIds(form);

    if (variantIds.length === 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    var submitter = event.submitter || form.querySelector('[type="submit"]');

    setBusy(form, true);

    addToCart(variantIds).then(function (response) {
      return response.json().then(function (json) {
        return { ok: response.ok, json: json };
      });
    }).then(function (result) {
      setBusy(form, false);

      if (!result.ok) {
        // The main product is left alone: a half added order is worse than none.
        reportError(form, result.json.description);
        return;
      }

      handledForms.add(form);
      form.requestSubmit(submitter);
    }).catch(function () {
      setBusy(form, false);
      reportError(form);
    });
  }, true);
})();

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * METAOBJECT COLOR SWATCHES
 *
 * The color groups rendered by snippets/custom-color-swatch-metaobject.liquid are line item properties, not variant
 * options, so <variant-picker> never sees them and nothing keeps their "LABEL * - Deep Pink" caption in sync with the
 * swatch the customer picked. That caption is all this does — the selected ring is drawn by the CSS from :checked.
 *
 * Delegated on the document because the form is re-rendered on variant changes and the quick buy drawer clones its own
 * copy of it out of a <template> on demand.
 * ---------------------------------------------------------------------------------------------------------------------
 */

(function () {
  'use strict';

  document.addEventListener('change', function (event) {
    var input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.closest('[data-color-metaobject-picker]')) {
      return;
    }

    var selector = input.closest('.product-form__option-selector');
    var caption = selector && selector.querySelector('[data-color-metaobject-value]');

    if (caption) {
      caption.textContent = input.value;
    }

    // The class is only carried so the markup states which swatch is picked; the theme styles it off :checked.
    Array.prototype.forEach.call(selector.querySelectorAll('.color-swatch'), function (swatch) {
      swatch.classList.toggle('is-selected', swatch.contains(input));
    });
  });
})();
